// ============================================================
// store/paramsStore.js
// EV Powertrain Simulator — 차량 파라미터 전역 상태
// 패턴: 팩토리(Factory) — createParamsStore(deps) 호출로 인스턴스 생성
// 사용법: import { createParamsStore } from '../store/paramsStore.js';
// Phase 3: Zustand 상태 관리 도입
//
// 사용법 (컴포넌트 내부):
//   import { useParamsStore } from '../store/paramsStore.js';
//   const paramsA    = useParamsStore(s => s.paramsA);
//   const setParamsA = useParamsStore(s => s.setParamsA);
//   const setParamsAField = useParamsStore(s => s.setParamsAField);
// ============================================================

/**
 * paramsStore — 차량 A/B 파라미터 + 프리셋 키
 *
 * persist 전략:
 *   현재: localStorage (ev-sim-params-v1)
 *   추후: persist 미들웨어 교체만으로 REST API 연동 가능
 *
 * import 방식 (CDN/ESM 빌드):
 *   import { create } from 'zustand';
 *   import { persist } from 'zustand/middleware';
 */

// ── LocalStorage 어댑터 (Zustand persist 없이도 동작) ────────
// [설계 의도] Zustand의 persist 미들웨어 대신 수동 직렬화를 택한 이유:
//   CDN/ESM 환경에서 Zustand 미들웨어를 번들 없이 쓰기 어렵고,
//   직렬화 대상(paramsA/B, vehicleKey)만 선택적으로 저장해 용량을 최소화하기 위함.
const STORAGE_KEY = 'ev-sim-params-v1';

/**
 * localStorage에서 파라미터 스냅샷을 읽어 파싱된 객체로 반환한다.
 *
 * JSON 파싱 오류나 localStorage 접근 불가(SSR, 샌드박스) 상황을 조용히 무시하고
 * null을 반환하여 상위 로직이 기본값으로 폴백하도록 한다.
 *
 * @returns {{ paramsA: object, paramsB: object, vehicleAKey: string, vehicleBKey: string }|null}
 */
function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

/**
 * 현재 스토어 상태 중 파라미터 관련 필드만 localStorage에 직렬화해 저장한다.
 *
 * 액션 함수·내부 리스너 등 직렬화 불가 멤버는 저장 대상에서 제외하고,
 * paramsA/B, vehicleKey, savedAt(타임스탬프)만 기록한다.
 *
 * @param {object} state  현재 스토어 상태 객체
 * @returns {boolean}     저장 성공 여부 (실패 시 false, 조용히 무시)
 */
function saveToStorage(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      paramsA:     state.paramsA,
      paramsB:     state.paramsB,
      vehicleAKey: state.vehicleAKey,
      vehicleBKey: state.vehicleBKey,
      savedAt:     new Date().toISOString(),
    }));
    return true;
  } catch { return false; }
}

// ── 초기값 (VEHICLE_PRESETS 로드 전 fallback) ────────────────
/**
 * localStorage 저장값과 DEFAULT_PARAMS를 머지하여 스토어 초기 상태를 구성한다.
 *
 * 머지 우선순위:
 *   1. 물리 계수 상수(FORCE_FROM_DEFAULT 목록)는 항상 DEFAULT_PARAMS 값 강제 적용
 *      → 캐시에 구 버전 계수가 남아있어도 최신 물리 모델을 보장
 *   2. PRESET_GAIN_VERSION 불일치 시 빌트인 프리셋의 Kp/Ki gain을 flush
 *      → 컨트롤러 계수 변경이 캐시로 인해 무시되는 버그 방지
 *   3. 커스텀 프리셋(VEHICLE_PRESETS에 없는 vehicleKey)은 gain flush 생략
 *      → 사용자가 수동으로 입력한 값을 보존
 *
 * @param {object} DEFAULT_PARAMS        전체 파라미터 기본값
 * @param {object} TESLA_M3_PARAMS       Tesla Model 3 빌트인 프리셋
 * @param {object} IONIQ6_PARAMS         Ioniq 6 빌트인 프리셋
 * @param {string} PRESET_GAIN_VERSION   현재 빌드의 gain 버전 스탬프
 * @param {object} VEHICLE_PRESETS       모든 빌트인 차량 프리셋 맵 { key: params }
 * @returns {{ vehicleAKey, vehicleBKey, paramsA, paramsB }}
 */
function buildInitialState(DEFAULT_PARAMS, TESLA_M3_PARAMS, IONIQ6_PARAMS, PRESET_GAIN_VERSION, VEHICLE_PRESETS) {
  const saved = loadFromStorage();
  // [Fix-Steinmetz] localStorage에 캐시된 params가 있어도 DEFAULT_PARAMS의 신규/변경 키를
  // 항상 앞에 깔고(default-first), 저장된 값을 덮는 방식으로 머지.
  // → VEHICLE_PRESETS/DEFAULT_PARAMS 계수를 고쳐도 캐시 때문에 무시되는 문제 방지.

  // [Fix-GainVersion-B1] vehicleKey를 받아 올바른 빌트인 프리셋 gain으로 flush.
  //   · saved=null (첫 로드): _gainVersion 스탬프를 찍은 fallback 반환
  //   · 버전 불일치 + 빌트인 프리셋: VEHICLE_PRESETS[vehicleKey] gain으로 교체
  //   · 버전 불일치 + 커스텀 프리셋: gain flush 생략 (사용자 수동값 보존), 버전만 갱신
  const mergeWithDefaults = (savedParams, fallback, vehicleKey) => {
    if (!savedParams) return { ...fallback, _gainVersion: PRESET_GAIN_VERSION ?? '' };
    const merged = { ...DEFAULT_PARAMS, ...savedParams };
    // [Fix-CacheOverride] 물리 단위계 계수는 캐시에 구 버전이 남아있으면 버그 원인이 됨.
    // 차량별 튜닝값이 아닌 내부 물리 계수는 항상 DEFAULT_PARAMS를 강제 적용.
    const FORCE_FROM_DEFAULT = [
      'steinmetz_kh', 'steinmetz_kc', 'steinmetz_n', 'B_peak_ref', 'V_iron',
      'poles',
      'wltp_ef_grid',  // [BUG-2-FIX] 배출계수 개정 시 캐시 구버전 무시, 항상 DEFAULT_PARAMS 적용
    ];
    FORCE_FROM_DEFAULT.forEach(k => {
      if (DEFAULT_PARAMS[k] !== undefined) merged[k] = DEFAULT_PARAMS[k];
    });
    // [Codegen-Gain] PRESET_GAIN_VERSION 불일치 시 Kp/Ki 캐시 flush.
    // gain source: 빌트인 프리셋이면 VEHICLE_PRESETS[vehicleKey] 우선 사용.
    // 커스텀 프리셋(vehicleKey가 없거나 VEHICLE_PRESETS에 없음)은 flush 생략.
    const GAIN_KEYS = ['Kp_id', 'Ki_id', 'Kp_iq', 'Ki_iq', 'Kp_spd', 'Ki_spd'];
    if (PRESET_GAIN_VERSION && savedParams._gainVersion !== PRESET_GAIN_VERSION) {
      const gainSource = VEHICLE_PRESETS?.[vehicleKey];
      if (gainSource) {
        GAIN_KEYS.forEach(k => {
          if (gainSource[k] !== undefined) merged[k] = gainSource[k];
        });
      }
      merged._gainVersion = PRESET_GAIN_VERSION;
    }
    return merged;
  };
  const vehicleAKey = saved?.vehicleAKey ?? 'tesla_m3';
  const vehicleBKey = saved?.vehicleBKey ?? 'ioniq_6';
  return {
    vehicleAKey,
    vehicleBKey,
    paramsA: mergeWithDefaults(saved?.paramsA, { ...DEFAULT_PARAMS, ...TESLA_M3_PARAMS }, vehicleAKey),
    paramsB: mergeWithDefaults(saved?.paramsB, { ...DEFAULT_PARAMS, ...IONIQ6_PARAMS   }, vehicleBKey),
  };
}

// ── 드라이브 파라미터 (시뮬레이션 입력) ─────────────────────
const DRIVE_INITIAL = {
  rlMassKg:        0,
  rlGrade:         0,
  rlThrottle:      100,
  launchThrottle:  100,
  reaccelThrottle: 100,
  rlSocScenario:   100,
  rlHeadwind:      0,
  reaccelV0:       60,
  reaccelV1:       100,
};

// ── 스토어 팩토리 ────────────────────────────────────────────
/**
 * 차량 파라미터 스토어 인스턴스를 생성한다.
 *
 * Zustand CDN 미사용 환경을 위한 경량 수동 구현체(observer 패턴).
 * 상태 변경 시 _notify()로 등록된 리스너에 새 상태를 동기 전달하며,
 * React 컴포넌트는 subscribe()를 통해 리렌더링을 구독한다.
 *
 * localStorage 영속화:
 *   - paramsA/B, vehicleKey는 persist() 호출 시 직렬화 저장
 *   - 드라이브 파라미터(rlMassKg 등)는 세션 메모리에만 보관(영속화 제외)
 *   - 앱 기동 시 buildInitialState()가 hydrate 역할을 겸함
 *
 * @param {object} deps
 * @param {object} deps.DEFAULT_PARAMS       전체 파라미터 기본값 객체
 * @param {object} deps.VEHICLE_PRESETS      빌트인 차량 프리셋 맵
 * @param {object} deps.TESLA_M3_PARAMS      Tesla Model 3 프리셋 오버라이드
 * @param {object} deps.IONIQ6_PARAMS        Ioniq 6 프리셋 오버라이드
 * @param {string} [deps.PRESET_GAIN_VERSION] 현재 빌드의 gain 버전 스탬프
 * @returns {object}  스토어 인스턴스 (getState, subscribe, persist, hydrate, 액션 메서드 포함)
 */
function createParamsStore(deps) {
  const { DEFAULT_PARAMS, VEHICLE_PRESETS, TESLA_M3_PARAMS, IONIQ6_PARAMS, PRESET_GAIN_VERSION } = deps;
  const init = buildInitialState(DEFAULT_PARAMS, TESLA_M3_PARAMS, IONIQ6_PARAMS, PRESET_GAIN_VERSION, VEHICLE_PRESETS);

  // --- 경량 스토어 구현 (Zustand 미사용 fallback) ---
  let state = {
    ...init,
    ...DRIVE_INITIAL,

    // ── 액션: 차량 A ──────────────────────────────

    /**
     * 차량 A 파라미터를 업데이트한다.
     *
     * updater가 함수이면 함수형 업데이트(이전 값 기반)를 수행하고,
     * 객체이면 현재 paramsA에 얕은 머지(shallow merge)를 적용한다.
     *
     * @param {((prev: object) => object) | object} updater  업데이터 함수 또는 부분 파라미터 객체
     */
    setParamsA(updater) {
      state.paramsA = typeof updater === 'function'
        ? updater(state.paramsA)
        : { ...state.paramsA, ...updater };
      state._notify();
    },

    /**
     * 차량 A 파라미터 중 단일 필드만 변경한다.
     *
     * 슬라이더·입력 필드 등 단일 값 변경 UI에서 setParamsA 호출 대비
     * 객체 생성 오버헤드를 줄이기 위한 편의 메서드.
     *
     * @param {string} key    변경할 파라미터 키 (예: 'mass_kg', 'motor_kw')
     * @param {*}      value  새 값
     */
    setParamsAField(key, value) {
      state.paramsA = { ...state.paramsA, [key]: value };
      state._notify();
    },

    // ── 액션: 차량 B ──────────────────────────────

    /**
     * 차량 B 파라미터를 업데이트한다.
     *
     * setParamsA와 동일한 동작 원리로, updater가 함수이면 함수형 업데이트,
     * 객체이면 얕은 머지를 수행한다.
     *
     * @param {((prev: object) => object) | object} updater  업데이터 함수 또는 부분 파라미터 객체
     */
    setParamsB(updater) {
      state.paramsB = typeof updater === 'function'
        ? updater(state.paramsB)
        : { ...state.paramsB, ...updater };
      state._notify();
    },

    /**
     * 차량 B 파라미터 중 단일 필드만 변경한다.
     *
     * @param {string} key    변경할 파라미터 키
     * @param {*}      value  새 값
     */
    setParamsBField(key, value) {
      state.paramsB = { ...state.paramsB, [key]: value };
      state._notify();
    },

    // ── 액션: 프리셋 키 ───────────────────────────

    /**
     * 차량 A의 프리셋을 변경하고, 빌트인 프리셋이면 파라미터도 즉시 교체한다.
     *
     * 빌트인 프리셋 선택 시 _gainVersion 스탬프를 함께 기록해
     * 이후 저장·복원 사이클에서 불필요한 gain flush가 반복되는 것을 방지한다.
     * 커스텀 프리셋(VEHICLE_PRESETS에 없는 key)은 파라미터를 교체하지 않는다.
     *
     * @param {string} key  VEHICLE_PRESETS의 키 또는 사용자 정의 프리셋 이름
     */
    setVehicleAKey(key) {
      state.vehicleAKey = key;
      if (VEHICLE_PRESETS[key]) {
        // [Fix-GainVersion-B3] 빌트인 프리셋 선택 시 _gainVersion 스탬프.
        // 미설정 시 saveToStorage → loadFromStorage 사이클에서 버전 불일치로
        // 매번 gain flush가 발생하는 문제 방지.
        state.paramsA = { ...VEHICLE_PRESETS[key], _gainVersion: PRESET_GAIN_VERSION ?? '' };
      }
      state._notify();
    },

    /**
     * 차량 B의 프리셋을 변경하고, 빌트인 프리셋이면 파라미터도 즉시 교체한다.
     *
     * setVehicleAKey와 동일한 동작 원리.
     *
     * @param {string} key  VEHICLE_PRESETS의 키 또는 사용자 정의 프리셋 이름
     */
    setVehicleBKey(key) {
      state.vehicleBKey = key;
      if (VEHICLE_PRESETS[key]) {
        state.paramsB = { ...VEHICLE_PRESETS[key], _gainVersion: PRESET_GAIN_VERSION ?? '' };
      }
      state._notify();
    },

    // ── 액션: 드라이브 파라미터 ──────────────────
    /** 도로부하 추가 질량(kg)을 설정한다. @param {number} v */
    setRlMassKg(v)        { state.rlMassKg = v;        state._notify(); },
    /** 도로부하 경사도(%)를 설정한다. @param {number} v */
    setRlGrade(v)         { state.rlGrade = v;          state._notify(); },
    /** 도로부하 스로틀(0~100%)을 설정한다. @param {number} v */
    setRlThrottle(v)      { state.rlThrottle = v;       state._notify(); },
    /** 발진 스로틀(0~100%)을 설정한다. @param {number} v */
    setLaunchThrottle(v)  { state.launchThrottle = v;   state._notify(); },
    /** 재가속 스로틀(0~100%)을 설정한다. @param {number} v */
    setReaccelThrottle(v) { state.reaccelThrottle = v;  state._notify(); },
    /** 도로부하 시나리오 SOC(0~100%)를 설정한다. @param {number} v */
    setRlSocScenario(v)   { state.rlSocScenario = v;    state._notify(); },
    /** 도로부하 맞바람 속도(km/h)를 설정한다. @param {number} v */
    setRlHeadwind(v)      { state.rlHeadwind = v;       state._notify(); },
    /** 재가속 시작 속도(km/h)를 설정한다. @param {number} v */
    setReaccelV0(v)       { state.reaccelV0 = v;        state._notify(); },
    /** 재가속 목표 속도(km/h)를 설정한다. @param {number} v */
    setReaccelV1(v)       { state.reaccelV1 = v;        state._notify(); },

    // ── 액션: 양쪽 동시 드라이브 모드 ────────────

    /**
     * 차량 A와 B의 드라이브 모드를 동시에 변경한다.
     *
     * 비교 탭에서 두 차량의 드라이브 모드를 동기화할 때 사용하며,
     * paramsA·paramsB를 각각 얕은 머지로 갱신해 구독자에게 한 번만 알린다.
     *
     * @param {string} mode  드라이브 모드 (예: 'normal', 'sport', 'eco')
     */
    setBothDriveMode(mode) {
      state.paramsA = { ...state.paramsA, drive_mode: mode };
      state.paramsB = { ...state.paramsB, drive_mode: mode };
      state._notify();
    },

    // ── 액션: persist ─────────────────────────────

    /**
     * 현재 paramsA/B, vehicleKey를 localStorage에 저장한다.
     *
     * saveToStorage()를 호출하는 퍼블릭 인터페이스.
     * 컴포넌트가 직접 localStorage를 건드리지 않도록 캡슐화한다.
     *
     * @returns {boolean}  저장 성공 여부
     */
    persist() { return saveToStorage(state); },

    /**
     * localStorage에서 파라미터를 읽어 현재 스토어 상태에 덮어쓴다.
     *
     * 앱 재마운트나 탭 복원 시 외부에서 수동으로 호출할 수 있다.
     * 일반적으로 buildInitialState()가 초기화 시 이미 이 역할을 담당하므로
     * 직접 호출이 필요한 경우는 드물다.
     */
    hydrate() {
      const saved = loadFromStorage();
      if (saved) {
        if (saved.paramsA)     state.paramsA = saved.paramsA;
        if (saved.paramsB)     state.paramsB = saved.paramsB;
        if (saved.vehicleAKey) state.vehicleAKey = saved.vehicleAKey;
        if (saved.vehicleBKey) state.vehicleBKey = saved.vehicleBKey;
        state._notify();
      }
    },

    // ── 내부: 구독자 알림 ─────────────────────────

    _listeners: new Set(),

    /**
     * 등록된 모든 구독자 함수에 현재 상태를 동기 전달한다.
     * 상태를 변경하는 모든 액션의 마지막에 호출된다.
     */
    _notify() {
      state._listeners.forEach(fn => fn(state));
    },

    /**
     * 상태 변경 구독을 등록하고, 해제 함수를 반환한다.
     *
     * React 컴포넌트의 useEffect 또는 useSyncExternalStore에서 호출한다.
     *
     * @param {(state: object) => void} fn  상태 변경 시 호출될 콜백
     * @returns {() => void}                구독 해제 함수
     */
    subscribe(fn) {
      state._listeners.add(fn);
      return () => state._listeners.delete(fn);
    },

    /**
     * 현재 스토어 상태 스냅샷을 반환한다.
     *
     * @returns {object}  현재 state 객체 (직접 변이 금지)
     */
    getState() { return state; },
  };

  return state;
}

export { createParamsStore };